import React, { useState, useEffect, useRef, useCallback } from 'react';
import api from '../../services/api';

const ROLE_COLORS = {
  sender: { bg: '#c94a1e', text: '#fff', label: 'Remitente' },
  agent:  { bg: '#2d6a4f', text: '#fff', label: 'Agente' },
  admin:  { bg: '#1a4e8c', text: '#fff', label: 'Admin' },
};

function Message({ msg, currentUserId }) {
  const isOwn = msg.own || msg.sender_id === currentUserId;
  const roleStyle = ROLE_COLORS[msg.sender_role] || ROLE_COLORS.sender;

  return (
    <div style={{
      display: 'flex',
      flexDirection: isOwn ? 'row-reverse' : 'row',
      gap: 8, marginBottom: 12, alignItems: 'flex-end',
    }}>
      {/* Avatar */}
      {!isOwn && (
        <div style={{
          width: 30, height: 30, borderRadius: '50%', flexShrink: 0,
          background: roleStyle.bg, display: 'flex', alignItems: 'center',
          justifyContent: 'center', fontSize: 11, fontWeight: 700, color: roleStyle.text,
        }}>
          {msg.sender_name?.charAt(0)?.toUpperCase() || '?'}
        </div>
      )}

      <div style={{ maxWidth: '72%' }}>
        {/* Sender name */}
        {!isOwn && (
          <div style={{ fontSize: 10.5, fontWeight: 700, color: roleStyle.bg, marginBottom: 3 }}>
            {msg.sender_name} · {roleStyle.label}
          </div>
        )}

        {/* Bubble */}
        <div style={{
          background: isOwn ? '#c94a1e' : '#fff',
          color: isOwn ? '#fff' : '#0f0d0b',
          borderRadius: isOwn ? '14px 14px 4px 14px' : '14px 14px 14px 4px',
          padding: '10px 14px',
          border: isOwn ? 'none' : '1px solid #e2dbd0',
          fontSize: 14, lineHeight: 1.5,
        }}>
          {msg.image_url && (
            <img
              src={msg.image_url}
              alt="Imagen"
              style={{ maxWidth: '100%', borderRadius: 8, marginBottom: msg.content ? 8 : 0, display: 'block', cursor: 'pointer' }}
              onClick={() => window.open(msg.image_url, '_blank')}
            />
          )}
          {msg.content && <span>{msg.content}</span>}
        </div>

        {/* Time */}
        <div style={{ fontSize: 10, color: '#b0a090', marginTop: 3, textAlign: isOwn ? 'right' : 'left' }}>
          {msg.created_at ? new Date(msg.created_at).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' }) : ''}
        </div>
      </div>
    </div>
  );
}

export default function ChatWindow({ orderRef, currentUser, onClose, embedded = false }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [image, setImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [connected, setConnected] = useState(false);
  const [typing, setTyping] = useState(null);
  const [sending, setSending] = useState(false);
  const wsRef = useRef(null);
  const bottomRef = useRef(null);
  const typingTimeout = useRef(null);

  const scrollToBottom = () => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => { scrollToBottom(); }, [messages]);

  const connectWS = useCallback(() => {
    const token = localStorage.getItem('token');
    if (!token || !orderRef) return;

    const wsUrl = `${process.env.REACT_APP_WS_URL || 'wss://api.guarapo.app'}/api/v1/chat/ws/${orderRef}?token=${token}`;
    const ws = new WebSocket(wsUrl);
    wsRef.current = ws;

    ws.onopen = () => {
      setConnected(true);
    };

    ws.onmessage = (e) => {
      const data = JSON.parse(e.data);

      if (data.type === 'history') {
        setMessages(data.messages || []);
      } else if (data.type === 'message') {
        setMessages(prev => [...prev, data]);
      } else if (data.type === 'typing') {
        setTyping(data.user);
        clearTimeout(typingTimeout.current);
        typingTimeout.current = setTimeout(() => setTyping(null), 3000);
      } else if (data.type === 'presence') {
        // Could show join/leave notification
      }
    };

    ws.onclose = () => {
      setConnected(false);
      // Reconnect after 3 seconds
      setTimeout(connectWS, 3000);
    };

    ws.onerror = () => {
      ws.close();
    };
  }, [orderRef]);

  useEffect(() => {
    connectWS();
    return () => {
      wsRef.current?.close();
    };
  }, [connectWS]);

  // Also load history via REST on mount
  useEffect(() => {
    api.get(`/api/v1/chat/${orderRef}/messages`)
      .then(r => setMessages(Array.isArray(r.data) ? r.data : []))
      .catch(() => {});
  }, [orderRef]);

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      setImage(ev.target.result);
      setImagePreview(ev.target.result);
    };
    reader.readAsDataURL(file);
  };

  const sendTyping = () => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'typing' }));
    }
  };

  const sendMessage = async () => {
    if (!input.trim() && !image) return;
    setSending(true);

    const msg = {
      type: 'message',
      content: input.trim(),
      image: image,
    };

    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(msg));
    } else {
      // REST fallback
      try {
        await api.post(`/api/v1/chat/${orderRef}/message`, {
          content: input.trim(),
          image_b64: image,
        });
      } catch (e) {
        console.error('Send error:', e);
      }
    }

    setInput('');
    setImage(null);
    setImagePreview(null);
    setSending(false);
  };

  const containerStyle = embedded ? {
    display: 'flex', flexDirection: 'column', height: '100%',
  } : {
    position: 'fixed', bottom: 20, right: 20,
    width: 380, height: 520,
    background: '#fff', borderRadius: 20,
    boxShadow: '0 8px 40px rgba(0,0,0,.18)',
    display: 'flex', flexDirection: 'column',
    zIndex: 1000, overflow: 'hidden',
    border: '1px solid #e2dbd0',
  };

  return (
    <>
      {!embedded && (
        <div
          onClick={onClose}
          style={{ position: 'fixed', inset: 0, zIndex: 999 }}
        />
      )}
      <div style={{ ...containerStyle, position: embedded ? 'relative' : 'fixed' }}>
        {/* Header */}
        <div style={{
          background: '#0f0d0b', padding: '14px 18px',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          flexShrink: 0,
        }}>
          <div>
            <div style={{ fontFamily: 'Georgia,serif', fontSize: 15, color: '#fff' }}>
              Chat · {orderRef}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 2 }}>
              <div style={{ width: 7, height: 7, borderRadius: '50%', background: connected ? '#2d6a4f' : '#b0a090' }} />
              <span style={{ fontSize: 11, color: 'rgba(255,255,255,.4)' }}>
                {connected ? 'Conectado' : 'Reconectando...'}
              </span>
            </div>
          </div>
          {!embedded && (
            <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,.5)', fontSize: 20, cursor: 'pointer' }}>✕</button>
          )}
        </div>

        {/* Messages */}
        <div style={{
          flex: 1, overflowY: 'auto', padding: '16px 14px',
          background: '#f9f6f1',
          display: 'flex', flexDirection: 'column',
        }}>
          {messages.length === 0 && (
            <div style={{ textAlign: 'center', color: '#7a7060', fontSize: 13, marginTop: 'auto', paddingBottom: 20 }}>
              <div style={{ fontSize: 32, marginBottom: 8 }}>💬</div>
              Inicia la conversación con el agente
            </div>
          )}
          {messages.map((msg, i) => (
            <Message key={msg.id || i} msg={msg} currentUserId={currentUser?.id} />
          ))}
          {typing && (
            <div style={{ fontSize: 12, color: '#7a7060', fontStyle: 'italic', marginBottom: 8 }}>
              {typing} está escribiendo...
            </div>
          )}
          <div ref={bottomRef} />
        </div>

        {/* Image preview */}
        {imagePreview && (
          <div style={{ padding: '8px 14px', borderTop: '1px solid #e2dbd0', background: '#fff' }}>
            <div style={{ position: 'relative', display: 'inline-block' }}>
              <img src={imagePreview} alt="Preview" style={{ height: 60, borderRadius: 8 }} />
              <button
                onClick={() => { setImage(null); setImagePreview(null); }}
                style={{ position: 'absolute', top: -6, right: -6, width: 20, height: 20, borderRadius: '50%', background: '#9b2c5e', border: 'none', color: '#fff', cursor: 'pointer', fontSize: 11, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                ✕
              </button>
            </div>
          </div>
        )}

        {/* Input */}
        <div style={{
          padding: '10px 12px', background: '#fff',
          borderTop: '1px solid #e2dbd0',
          display: 'flex', gap: 8, alignItems: 'flex-end',
          flexShrink: 0,
        }}>
          {/* Image button */}
          <label style={{ flexShrink: 0, width: 36, height: 36, borderRadius: 8, border: '1px solid #e2dbd0', background: '#f9f6f1', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', fontSize: 16 }}>
            📎
            <input type="file" accept="image/*" style={{ display: 'none' }} onChange={handleImageUpload} />
          </label>

          {/* Text input */}
          <textarea
            value={input}
            onChange={e => { setInput(e.target.value); sendTyping(); }}
            onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); } }}
            placeholder="Escribe un mensaje..."
            style={{
              flex: 1, padding: '8px 12px', border: '1px solid #e2dbd0',
              borderRadius: 10, fontSize: 14, outline: 'none', resize: 'none',
              minHeight: 36, maxHeight: 100, lineHeight: 1.4,
              fontFamily: 'inherit',
            }}
            rows={1}
          />

          {/* Send button */}
          <button
            onClick={sendMessage}
            disabled={sending || (!input.trim() && !image)}
            style={{
              flexShrink: 0, width: 36, height: 36, borderRadius: 8, border: 'none',
              background: (sending || (!input.trim() && !image)) ? '#e2dbd0' : '#c94a1e',
              color: '#fff', cursor: (sending || (!input.trim() && !image)) ? 'not-allowed' : 'pointer',
              fontSize: 16, display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
            →
          </button>
        </div>
      </div>
    </>
  );
}
